import { Paper, Typography, TextField, Grid, IconButton, Button, Select, MenuItem, Box } from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import SymbolField from '../Util/SymbolField'

const CHARACTERISTICS = [["BR", "Brawn"], ["AG", "Agility"], ["INT", "Intellect"], ["CUN", "Cunning"], ["WIL", "Willpower"], ["PR", "Presence"]];
const ADVERSARY_TYPES = ["Minion", "Rival", "Nemesis"];

const num = (v) => (v === "" || isNaN(v) ? v : Number(v));

export const newNpcProfile = (name) => ({
    Name: name || "",
    AdversaryType: "Rival",
    Characteristics: { BR: 1, AG: 1, INT: 1, CUN: 1, WIL: 1, PR: 1 },
    Attributes: { Soak: 0, WoundThreshold: 0 },
    Skills: {},
    Talents: [],
    Abilities: [],
    Weapons: [],
    Gear: "",
});

const RemoveButton = ({ onClick }) => (
    <IconButton onClick={onClick} sx={{ maxWidth: "30px" }} size="small" className="no-print">
        <CloseIcon />
    </IconButton>
);

const NpcProfileEditor = ({ profile, itemName, onChange, skills, talents, weapons, qualities }) => {
    if (!profile) {
        return (
            <Button onClick={() => onChange(newNpcProfile(itemName))} className="no-print" sx={{ marginTop: "10px" }}>
                Add NPC Profile
            </Button>
        );
    }

    const set = (patch) => onChange({ ...profile, ...patch });
    const setOptional = (key, value) => {
        const next = { ...profile, [key]: num(value) };
        if (value === "") { delete next[key]; }
        onChange(next);
    };
    const characteristics = profile.Characteristics || {};
    const attributes = profile.Attributes || {};
    const setAttribute = (key, value) => {
        const next = { ...attributes, [key]: num(value) };
        if (value === "" && key === "StrainThreshold") { delete next[key]; }
        set({ Attributes: next });
    };
    const defense = attributes.Defense || attributes.Defence || [0, 0];
    const setDefense = (i, value) => {
        const next = [defense[0] ?? 0, defense[1] ?? 0];
        next[i] = num(value);
        const { Defence, ...rest } = attributes;
        set({ Attributes: { ...rest, Defense: next } });
    };

    const skillNames = [...skills].map((s) => s.skill).sort((a, b) => a.localeCompare(b));
    const skillsIsArray = Array.isArray(profile.Skills);
    const skillEntries = skillsIsArray ? [] : Object.entries(profile.Skills || {});
    const setSkillEntries = (entries) => set({ Skills: Object.fromEntries(entries) });
    const setSkillList = (list) => set({ Skills: list });

    const talentList = profile.Talents || [];
    const setTalents = (list) => set({ Talents: list });
    const talentLabel = (talent) => talent?.Key
        ? `${talents.find((t) => t.Key === talent.Key)?.Name || talent.Key} ${talent.Ranks || ""}`.trim()
        : talent;

    const abilityList = profile.Abilities || [];
    const setAbilities = (list) => set({ Abilities: list });
    const updateAbility = (index, value) => setAbilities(abilityList.map((a, i) => (i === index ? value : a)));

    const weaponList = profile.Weapons || [];
    const setWeapons = (list) => set({ Weapons: list });
    const updateWeapon = (index, value) => setWeapons(weaponList.map((w, i) => (i === index ? value : w)));
    const sortedWeapons = [...weapons].sort((a, b) => (a.Name || "").localeCompare(b.Name || ""));
    const customizeWeapon = (key) => {
        const base = weapons.find((w) => w.Key === key) || {};
        const qualityNames = [].concat(base.Qualities || []).map((q) => {
            if (!q?.Key) { return q; }
            const name = (qualities.find((x) => x.Key === q.Key)?.Name || q.Key).replace(/ Quality$/, "");
            return `${name}${q.Count ? " " + q.Count : ""}`;
        });
        return {
            Name: base.Name || key,
            Skill: skills.find((s) => s.Key === base.SkillKey)?.skill || "",
            Damage: base.Damage ?? (base.DamageAdd !== undefined ? "+" + base.DamageAdd : ""),
            Crit: base.Crit || "",
            Range: base.Range || base.RangeValue || "",
            Qualities: qualityNames,
        };
    };

    return (
        <Paper sx={{ marginTop: "10px", padding: "10px" }}>
            <Grid container spacing={1} alignItems="center">
                <Grid item xs={12} sm>
                    <Typography variant="h6">NPC Profile</Typography>
                </Grid>
                <Grid item>
                    <Button color="error" onClick={() => onChange(undefined)} className="no-print">Remove NPC Profile</Button>
                </Grid>
            </Grid>
            <Grid container spacing={1} sx={{ marginTop: "5px" }}>
                <Grid item xs={12} sm={6}>
                    <TextField fullWidth label="Profile Name" value={profile.Name || ""} onChange={(e) => set({ Name: e.target.value })} />
                </Grid>
                <Grid item xs={6} sm={3}>
                    <Select fullWidth value={profile.AdversaryType || ""} displayEmpty onChange={(e) => set({ AdversaryType: e.target.value })}>
                        <MenuItem value=""><em>Adversary Type</em></MenuItem>
                        {ADVERSARY_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                    </Select>
                </Grid>
                <Grid item xs={6} sm={3}>
                    <TextField fullWidth label="Silhouette" value={profile.Silhouette ?? ""} onChange={(e) => setOptional("Silhouette", e.target.value)} />
                </Grid>
            </Grid>

            <Grid container spacing={1} sx={{ marginTop: "5px" }}>
                {CHARACTERISTICS.map(([key, label]) => (
                    <Grid item xs={4} sm={2} key={key}>
                        <TextField
                            fullWidth
                            label={label}
                            value={characteristics[key] ?? ""}
                            onChange={(e) => set({ Characteristics: { ...characteristics, [key]: num(e.target.value) } })}
                        />
                    </Grid>
                ))}
            </Grid>

            <Grid container spacing={1} sx={{ marginTop: "5px" }}>
                <Grid item xs={6} sm={2.4}>
                    <TextField fullWidth label="Soak" value={attributes.Soak ?? ""} onChange={(e) => setAttribute("Soak", e.target.value)} />
                </Grid>
                <Grid item xs={6} sm={2.4}>
                    <TextField fullWidth label="Wound Threshold" value={attributes.WoundThreshold ?? ""} onChange={(e) => setAttribute("WoundThreshold", e.target.value)} />
                </Grid>
                <Grid item xs={6} sm={2.4}>
                    <TextField fullWidth label="Strain Threshold" placeholder="None" value={attributes.StrainThreshold ?? ""} onChange={(e) => setAttribute("StrainThreshold", e.target.value)} />
                </Grid>
                <Grid item xs={3} sm={2.4}>
                    <TextField fullWidth label="Ranged Def" value={defense[0] ?? ""} onChange={(e) => setDefense(0, e.target.value)} />
                </Grid>
                <Grid item xs={3} sm={2.4}>
                    <TextField fullWidth label="Melee Def" value={defense[1] ?? ""} onChange={(e) => setDefense(1, e.target.value)} />
                </Grid>
            </Grid>

            <Typography sx={{ marginTop: "10px" }}>Skills:</Typography>
            <Grid container>
                {!skillsIsArray && skillEntries.map(([skillName, ranks], index) => (
                    <Grid item key={index} sx={{ margin: "7.5px", display: "flex", alignItems: "center" }}>
                        <Select
                            value={skillName}
                            onChange={(e) => setSkillEntries(skillEntries.map((entry, i) => (i === index ? [e.target.value, ranks] : entry)))}
                            sx={{ minWidth: "160px" }}
                        >
                            {!skillNames.includes(skillName) && <MenuItem value={skillName}>{skillName}</MenuItem>}
                            {skillNames.map((name) => (
                                <MenuItem key={name} value={name} disabled={name !== skillName && skillEntries.some(([n]) => n === name)}>{name}</MenuItem>
                            ))}
                        </Select>
                        <TextField
                            label="Ranks"
                            value={ranks ?? ""}
                            onChange={(e) => setSkillEntries(skillEntries.map((entry, i) => (i === index ? [skillName, num(e.target.value)] : entry)))}
                            sx={{ maxWidth: "70px" }}
                        />
                        <RemoveButton onClick={() => setSkillEntries(skillEntries.filter((_, i) => i !== index))} />
                    </Grid>
                ))}
                {skillsIsArray && profile.Skills.map((skill, index) => (
                    <Grid item key={index} sx={{ margin: "7.5px", display: "flex", alignItems: "center" }}>
                        <TextField
                            label="Skill"
                            value={skill?.Key ? (skills.find((s) => s.Key === skill.Key)?.skill || skill.Key) : (skill || "")}
                            disabled={!!skill?.Key}
                            onChange={(e) => setSkillList(profile.Skills.map((s, i) => (i === index ? e.target.value : s)))}
                            sx={{ maxWidth: "180px" }}
                        />
                        <RemoveButton onClick={() => setSkillList(profile.Skills.filter((_, i) => i !== index))} />
                    </Grid>
                ))}
                <Grid item sx={{ display: "flex", alignItems: "center" }}>
                    <Button
                        className="no-print"
                        onClick={() => skillsIsArray
                            ? setSkillList([...profile.Skills, ""])
                            : setSkillEntries([...skillEntries, [skillNames.find((n) => !skillEntries.some(([s]) => s === n)) || "New Skill", 1]])}
                    >
                        Add Skill
                    </Button>
                </Grid>
            </Grid>

            <Typography sx={{ marginTop: "10px" }}>Talents:</Typography>
            <Grid container>
                {talentList.map((talent, index) => (
                    <Grid item key={index} sx={{ margin: "7.5px", display: "flex", alignItems: "center" }}>
                        <TextField
                            label="Talent"
                            value={talentLabel(talent) || ""}
                            disabled={!!talent?.Key}
                            onChange={(e) => setTalents(talentList.map((t, i) => (i === index ? e.target.value : t)))}
                        />
                        <RemoveButton onClick={() => setTalents(talentList.filter((_, i) => i !== index))} />
                    </Grid>
                ))}
                <Grid item sx={{ display: "flex", alignItems: "center" }}>
                    <Button onClick={() => setTalents([...talentList, ""])} className="no-print">Add Talent</Button>
                </Grid>
            </Grid>

            <Typography sx={{ marginTop: "10px" }}>Abilities:</Typography>
            {abilityList.map((ability, index) => (
                <Box key={index} sx={{ display: "flex", alignItems: "flex-start", gap: "8px", marginTop: "8px" }}>
                    {typeof ability === "object" && ability !== null ? (
                        <>
                            <TextField
                                label="Ability Name"
                                value={ability.Name || ""}
                                onChange={(e) => updateAbility(index, { ...ability, Name: e.target.value })}
                                sx={{ minWidth: "160px" }}
                            />
                            <Box sx={{ flex: 1 }}>
                                <SymbolField
                                    fullWidth
                                    multiline
                                    label="Description"
                                    value={ability.Description || ""}
                                    onChange={(e) => updateAbility(index, { ...ability, Description: e.target.value })}
                                />
                            </Box>
                        </>
                    ) : (
                        <Box sx={{ flex: 1 }}>
                            <SymbolField
                                fullWidth
                                multiline
                                label="Ability"
                                value={ability || ""}
                                onChange={(e) => updateAbility(index, e.target.value)}
                            />
                        </Box>
                    )}
                    <RemoveButton onClick={() => setAbilities(abilityList.filter((_, i) => i !== index))} />
                </Box>
            ))}
            <Button onClick={() => setAbilities([...abilityList, { Name: "", Description: "" }])} className="no-print">Add Ability</Button>

            <Typography sx={{ marginTop: "10px" }}>Weapons:</Typography>
            {weaponList.map((weapon, index) => (
                <Paper key={index} variant="outlined" sx={{ marginTop: "8px", padding: "5px" }}>
                    {typeof weapon === "string" ? (
                        <Box sx={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                            <RemoveButton onClick={() => setWeapons(weaponList.filter((_, i) => i !== index))} />
                            <Select value={weapon} onChange={(e) => updateWeapon(index, e.target.value)} sx={{ minWidth: "220px" }}>
                                {!weapons.some((w) => w.Key === weapon) && <MenuItem value={weapon}>{weapon}</MenuItem>}
                                {sortedWeapons.map((w) => <MenuItem key={w.Key} value={w.Key}>{w.Name}</MenuItem>)}
                            </Select>
                            <Button onClick={() => updateWeapon(index, customizeWeapon(weapon))} className="no-print">Customize</Button>
                        </Box>
                    ) : (
                        <Grid container spacing={1} alignItems="center">
                            <Grid item>
                                <RemoveButton onClick={() => setWeapons(weaponList.filter((_, i) => i !== index))} />
                            </Grid>
                            {[["Name", "180px"], ["Skill", "150px"], ["Damage", "90px"], ["Crit", "70px"], ["Range", "110px"]].map(([field, width]) => (
                                <Grid item key={field}>
                                    <TextField
                                        label={field}
                                        value={weapon[field] ?? ""}
                                        onChange={(e) => updateWeapon(index, { ...weapon, [field]: e.target.value })}
                                        sx={{ maxWidth: width }}
                                    />
                                </Grid>
                            ))}
                            <Grid item xs={12} sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: "4px" }}>
                                <Typography>Qualities:</Typography>
                                {(weapon.Qualities || []).map((quality, qI) => (
                                    <Box key={qI} sx={{ display: "flex", alignItems: "center" }}>
                                        <TextField
                                            label="Quality"
                                            value={quality?.Key ? `${quality.Key}${quality.Count ? " " + quality.Count : ""}` : (quality || "")}
                                            onChange={(e) => updateWeapon(index, { ...weapon, Qualities: weapon.Qualities.map((q, i) => (i === qI ? e.target.value : q)) })}
                                            sx={{ maxWidth: "140px" }}
                                        />
                                        <RemoveButton onClick={() => updateWeapon(index, { ...weapon, Qualities: weapon.Qualities.filter((_, i) => i !== qI) })} />
                                    </Box>
                                ))}
                                <Button onClick={() => updateWeapon(index, { ...weapon, Qualities: [...(weapon.Qualities || []), ""] })} className="no-print">Add Quality</Button>
                            </Grid>
                        </Grid>
                    )}
                </Paper>
            ))}
            <Button
                onClick={() => setWeapons([...weaponList, { Name: "", Skill: "", Damage: "", Crit: "", Range: "", Qualities: [] }])}
                className="no-print"
            >
                Add Custom Weapon
            </Button>
            {sortedWeapons[0] &&
                <Button onClick={() => setWeapons([...weaponList, sortedWeapons[0].Key])} className="no-print">Add Weapon From List</Button>
            }

            <Box sx={{ marginTop: "10px" }}>
                <SymbolField
                    fullWidth
                    multiline
                    label="Gear"
                    value={profile.Gear || ""}
                    onChange={(e) => set({ Gear: e.target.value })}
                />
            </Box>
        </Paper>
    );
};

export default NpcProfileEditor;
